import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import PurchaseList from './PurchaseList';
import BASEURL from '../API/Config';

const PurchaseOrder = () => {
  const [formData, setFormData] = useState({
    vendor: '',
    orderdate: '',
    items: [{ itemname: '', Quantity: 0, price: 0 }],
    totalAmount: 0,
    tax: 0,
  });

  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [showList, setShowList] = useState(false);

  const token = localStorage.getItem('token');

  // Fetch Vendors
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await fetch(`${BASEURL}/master/getvendor1`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error('Failed to fetch vendors');

        const data = await response.json();
        setVendors(data);
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load vendors.' });
      }
    };
    fetchVendors();
  }, [token]);

  // Fetch Products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${BASEURL}/Products`);
        if (!response.ok) throw new Error('Failed to fetch products');
        const data = await response.json();
        setProducts(Array.isArray(data.products) ? data.products : []);
      } catch (error) {
        Swal.fire('Error', 'Failed to load products.', 'error');
      }
    };

    fetchProducts();
  }, []);

  // Handle Input Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  // Handle Item Change
  const handleItemChange = (index, field, value) => {
    const updatedItems = formData.items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setFormData((prevState) => ({ ...prevState, items: updatedItems }));
  };

  // Add Item Row
  const handleAddItem = () => {
    setFormData((prevState) => ({
      ...prevState,
      items: [...prevState.items, { itemname: '', Quantity: 0, price: 0 }],
    }));
  };

  // Remove Item Row
  const handleRemoveItem = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      items: prevState.items.filter((_, i) => i !== index),
    }));
  };

  // Calculate Total Amount
  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.Quantity * item.price, 0);
    const taxAmount = (subtotal * formData.tax) / 100;
    return subtotal + taxAmount;
  };

  useEffect(() => {
    setFormData((prevState) => ({
      ...prevState,
      totalAmount: calculateTotal(),
    }));
  }, [formData.items, formData.tax]);

  // Handle Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const { vendor, orderdate, items, tax } = formData;
    const totalAmount = calculateTotal();

    if (!vendor || !orderdate || items.length === 0 || totalAmount === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please fill in all required fields and add valid items.',
      });
      return;
    }

    // Prepare Items Array in Correct Format
    const formattedItems = items.map((item) => ({
      itemname: item.itemname, // product ID
      Quantity: Number(item.Quantity),
      price: Number(item.price),
    }));

    // Construct the Final Data Object
    const requestData = {
      vendor,
      orderdate,
      items: formattedItems,
      tax: Number(tax),
      totalAmount,
    };

    try {
      const response = await fetch(`${BASEURL}/master/createpurchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) throw new Error('Failed to create purchase order');

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Purchase order created successfully!',
      });

      // Reset Form After Success
      setFormData({
        vendor: '',
        orderdate: '',
        items: [{ itemname: '', Quantity: 0, price: 0 }],
        totalAmount: 0,
        tax: 0,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to create purchase order.',
      });
    }
  };

  // Toggle between Purchase Order and Purchase List
  const handleToggleView = () => {
    setShowList(!showList);
  };

  return (
    <div className="container mt-5">
      <h3 className="mb-4 text-center text-primary">
        {showList ? 'Purchase List' : 'Purchase Order'}
      </h3>
      <button className="btn btn-primary mb-4" onClick={handleToggleView}>
        {showList ? 'Create Purchase Order' : 'View Purchase List'}
      </button>

      {!showList && (
        <form
          onSubmit={handleSubmit}
          className="p-4 shadow-sm rounded"
          style={{ backgroundColor: '#f8f9fa' }}
        >
          <div className="form-group mb-3">
            <label className="fw-semibold">Vendor</label>
            <select
              name="vendor"
              className="form-control"
              value={formData.vendor}
              onChange={handleInputChange}
            >
              <option value="">Select Vendor</option>
              {vendors.map((vendor) => (
                <option key={vendor._id} value={vendor._id}>
                  {vendor.firstname} {vendor.lastname}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group mb-3">
            <label className="fw-semibold">Order Date</label>
            <input
              type="date"
              name="orderdate"
              className="form-control"
              value={formData.orderdate}
              onChange={handleInputChange}
            />
          </div>

          <div className="mb-4">
            <h5>Items</h5>
            {formData.items.map((item, index) => (
              <div key={index} className="row mb-3 align-items-end">
                <div className="col-md-4">
                  <label className="fw-semibold">Item Name</label>
                  <Select
                    options={products.map((product) => ({
                      value: product._id,
                      label: product.title,
                    }))}
                    onChange={(selectedOption) =>
                      handleItemChange(index, 'itemname', selectedOption.value)
                    }
                    placeholder="Select Product"
                    isSearchable
                  />
                </div>

                <div className="col-md-3">
                  <label className="fw-semibold">Quantity</label>
                  <input
                    type="number"
                    className="form-control"
                    value={item.Quantity}
                    onChange={(e) =>
                      handleItemChange(index, 'Quantity', e.target.value)
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="fw-semibold">Price</label>
                  <input
                    type="number"
                    className="form-control"
                    value={item.price}
                    onChange={(e) =>
                      handleItemChange(index, 'price', e.target.value)
                    }
                  />
                </div>

                <div className="col-md-2">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleRemoveItem(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleAddItem}
            >
              Add Item
            </button>
          </div>

          <button type="submit" className="btn btn-success w-50">
            Submit
          </button>
        </form>
      )}

      {showList && <PurchaseList />}
    </div>
  );
};

export default PurchaseOrder;
